import { EN } from 'src/i18n'
import Model, { allQueries } from 'src/models/Model'
import { markRaw } from 'vue'

function hasProperty (holder, key) {
  return typeof holder !== 'undefined' && Object.prototype.hasOwnProperty.call(holder, key)
}

function proxyModels (key) {
  if (!hasProperty(this.$options.props, key) && !hasProperty(this.$options.computed, key) && !hasProperty(this.$options.methods, key)) {
    Object.defineProperty(this, key, {
      get: () => this.$models[key],
      enumerable: true,
      configurable: true
    })
  } else {
    throw new Error('Naming collision, unable to mount VueModel query: ' + key)
  }
}

function proxyConfiguration (key) {
  if (!hasProperty(this.$options.props, key) && !hasProperty(this.$options.computed, key) && !hasProperty(this.$options.methods, key)) {
    Object.defineProperty(this, key, {
      get: () => this.$configuration[key],
      enumerable: true,
      configurable: true
    })
  } else {
    throw new Error('Naming collision, unable to mount VueModel configuration: ' + key)
  }
}

function proxyContext (key) {
  if (!hasProperty(this.$options.props, key) && !hasProperty(this.$options.computed, key) && !hasProperty(this.$options.methods, key)) {
    Object.defineProperty(this, key, {
      get: () => this.$context[key],
      enumerable: true,
      configurable: true
    })
  } else {
    throw new Error('Naming collision, unable to mount VueModel context: ' + key)
  }
}

export const VueModelMixin = {
  inject: {
    /**
     * This is only used for PreviewPage and for nothing else. See PreviewPage.vue for more information.
     */
    previewContext: {
      default: null
    }
  },
  data () {
    return {
      $models: markRaw({}),
      $configuration: null,
      $context: null
    }
  },
  beforeCreate () {
    if (!this.$options.models && !this.$options.configuration && !this.$options.context) {
      return
    }

    this.$models = {}
    this.$configuration = {}
    this.$context = {}

    _.each(this.$options.models, (options, name) => {
      if (_.isObject(options) && options.skip) {
        this.$models[name] = null
        proxyModels.call(this, name)
        return
      }

      if (options === true) {
        options = 'default'
      }

      if (_.isString(options)) {
        options = { fields: options }
      }

      const query = allQueries[name]

      if (!query) {
        throw new Error(`No query found that matches the name [${name}] found in VueModel component definition [${this.$options.name}]`)
      }

      const results = query.results

      if (_.keys(results).length !== 1) {
        console.log(`Expected exactly one result type, found: [${_.keys(results)}], using first`)
      }
      const resultName = _.keys(results)[0]

      const isCollectionType = _.isArray(results[resultName].data || results[resultName])

      const Model = isCollectionType ? ((results[resultName].data && results[resultName].data[0]) || results[resultName][0]) : results[resultName]

      let variablesFunction
      if (typeof options.variables === 'function') {
        variablesFunction = options.variables
        options.variables = null
        options.variablesFunction = variablesFunction
      }

      options.queryName = name
      options.key = name
      options.isCollection = isCollectionType

      const model = new Model({ options })
      model.hasVueParent = true

      this.$models[name] = model

      proxyModels.call(this, name)
    })

    if (this.$options.configuration) {
      this.$configuration = Model.configuration
    }
    _.each(this.$options.configuration, name => {
      if (!this.$configuration[name]) {
        console.warn(`ERROR: UNKNOWN CONFIGURATION TYPE ${name} IN <${this.$options.name} />`)
      }
      proxyConfiguration.call(this, name)
    })
    if (this.$options.context) {
      this.$context = Model.context
    }
    _.each(this.$options.context, name => {
      if (this.$context[name] === undefined) {
        console.warn(`ERROR: UNKNOWN CONTEXT TYPE: ${name} IN <${this.$options.name} />`)
      }
      proxyContext.call(this, name)
    })
  },
  created () {
    if (!this.$models) {
      return
    }

    // Make reactive
    _.each(this.$models, (model, name) => {
      if (!model) return

      if (this.previewContext?.languageOverrideForNewModels && this.previewContext.languageOverrideForNewModels !== EN) {
        model.options.forceLanguage = this.previewContext.languageOverrideForNewModels
      }

      let previousVariables = null
      function handleVariablesChange (variables) {
        const newVariables = { ...model.options.variables, ...variables }

        if (previousVariables && _.isEqual(newVariables, previousVariables)) return

        previousVariables = _.clone(newVariables)

        if (newVariables.skip) return

        model.options.variables = newVariables
        model.fetch(model.options.force)
      }

      if (model.options.variablesFunction) {
        model.options.variables = model.options.variablesFunction.call(this)
        const unwatch = this.$watch(() => model.options.variablesFunction.call(this), handleVariablesChange, {
          immediate: model.options.immediate,
          deep: true
        })
        if (!this.unwatchers) {
          this.unwatchers = []
        }
        this.unwatchers.push(unwatch)
      }
      if (!model.options.skip && !(model.options.variables?.skip) && ((model.isCollection && model.options.immediate !== false) || model.options.immediate)) {
        model.fetch(model.options.force)
      }

      this.$data.$models[name] = model
    })
  },
  beforeUnmount () {
    while (this.unwatchers && this.unwatchers.length) {
      const unwatcher = this.unwatchers.pop()
      unwatcher()
    }
    if (this.$data.$models) {
      _(this.$data.$models).values().each(model => model.destroy && model.destroy())
    }
  },
  unmounted () {
    _.each(this._watchers, watcher => {
      if (watcher.model) {
        watcher.model.handleDestroyedWatcher(watcher)
      }
    })
  }
}

export default {
  install (app) {
    app.mixin(VueModelMixin)

    // Define custom option merge strategies for mixins

    /**
     * Combine any `configuration` arrays present in both the component and mixin(s) into
     * unique array of values.
     * @param {string[] | undefined} toArrayVal The new value being merged
     * @param {string[] | undefined} fromArrayVal The existing value being merged into
     * @returns {string[] | undefined} The combined array of values
     * @example
     * configuration: ['currentCircuit', 'currentSite']
     * */
    app.config.optionMergeStrategies.configuration = function (toArrayVal, fromArrayVal) {
      if (_.isNil(toArrayVal) && _.isNil(fromArrayVal)) {
        return undefined
      }
      return _([toArrayVal, fromArrayVal]).flatten().compact().uniq().value()
    }

    /**
     * Combine any `context` arrays present in both the component and mixin(s) into
     * unique array of values.
     * @param {string[] | undefined} toArrayVal The new value being merged
     * @param {string[] | undefined} fromArrayVal The existing value being merged into
     * @returns {string[] | undefined} The combined array of values
     * @example
     * context: ['currentUser']
     */
    app.config.optionMergeStrategies.context = function (toArrayVal, fromArrayVal) {
      if (_.isNil(toArrayVal) && _.isNil(fromArrayVal)) {
        return undefined
      }
      return _([toArrayVal, fromArrayVal]).flatten().compact().uniq().value()
    }

    /**
     * Combine any `models` objects present in both the component and mixin(s) into a deep
     * merged object. The host component's values override conflicting non-mergeable mixin values.
     *
     * NOTE: Always make sure to test that the models object was merged as intended.
     * @param {Record<string, any> | undefined} toArrayObj The new value being merged
     * @param {Record<string, any> | undefined} fromArrayObj The existing value being merged into
     * @returns {Record<string, any> | undefined} A combined models object
     * */
    app.config.optionMergeStrategies.models = function (toModelsObj, fromModelsObj) {
      if (_.isNil(toModelsObj) && _.isNil(fromModelsObj)) {
        return undefined
      }
      // The host component's values should take precedence over the mixin's values
      return _.merge({}, toModelsObj, fromModelsObj)
    }
  }
}
